// Full-screen player controls visibility (spec 2026-10-24-fullscreen-player-controls §2; web twin of Android
// PlayerControlsVisibility). Figma: Shared / Player / Controls 4351:2706, Platform=Web variants.
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import type { FocusEvent, KeyboardEvent, PointerEvent } from "react";

/** Why the controls must stay up. "focus" = keyboard focus inside the controls (web's TalkBack twin). */
export type HoldReason = "paused" | "seeking" | "upNext" | "sheet" | "focus";

export const PLAYER_CONTROLS_HIDE_AFTER_MS = 4_000;

/** What counts as "a control" for taps and focus: a touch on one never toggles, keyboard focus on one holds. */
export const PLAYER_CONTROL_SELECTOR = "button, a[href], input, select, textarea, [role='slider'], [data-player-control]";

/** Focus this soon after a pointer press (and no key since) is that press's focus: it never holds. */
const POINTER_FOCUS_MS = 1_500;

/**
 * The document's input modality (like the browser's own :focus-visible heuristic), shared by every player: the last
 * input was a key, and when the last pointer press was (Date.now()). Watched from module load on the main document
 * (and on a root's own document once seen), so a player mounted by a click (MediaViewer opened from a tile, which
 * then focuses its Close) already knows that click.
 */
const modality = { keyboard: false, lastPointer: Number.NEGATIVE_INFINITY };
const watchedDocs = new WeakSet<Document>();
const markKey = () => {
  modality.keyboard = true;
};
const markPointer = () => {
  modality.keyboard = false;
  modality.lastPointer = Date.now();
};
function watchModality(doc: Document) {
  if (watchedDocs.has(doc)) return;
  watchedDocs.add(doc);
  doc.addEventListener("keydown", markKey, true);
  doc.addEventListener("pointerdown", markPointer, true);
}
if (typeof document !== "undefined") watchModality(document);

export interface PlayerControlsVisibility {
  visible: boolean;
  /** Shows and restarts the countdown. */
  show(): void;
  /** A tap on the video: shown -> hidden at once (even while held, except "focus"); hidden -> shown. */
  toggle(): void;
  /** Any use of the controls: keeps them up and restarts the countdown. */
  interacted(): void;
  /** Holds stop the countdown (and show the controls); releasing the last one restarts it. */
  hold(reason: HoldReason, on: boolean): void;
  /** Spread on the full-screen root. */
  bind: {
    /** The root: its document is where keys and pointer presses are watched (a Document PiP window has its own). */
    ref(el: HTMLElement | null): void;
    onPointerMove(e: PointerEvent<HTMLElement>): void;
    onPointerDown(e: PointerEvent<HTMLElement>): void;
    onKeyDown(e: KeyboardEvent<HTMLElement>): void;
    onFocusCapture(e: FocusEvent<HTMLElement>): void;
    onBlurCapture(e: FocusEvent<HTMLElement>): void;
  };
}

const onControl = (target: EventTarget | null, root: EventTarget | null): boolean => {
  if (!(target instanceof Element) || !(root instanceof Element)) return false;
  const hit = target.closest(PLAYER_CONTROL_SELECTOR);
  return hit != null && root.contains(hit);
};

/** The browser's own "focus came from the keyboard" (sequential navigation, also out of a cross-origin iframe). */
const focusVisible = (el: EventTarget | null): boolean => {
  try {
    return el instanceof Element && el.matches(":focus-visible");
  } catch {
    return false; // an engine without :focus-visible
  }
};

/**
 * Shown on entering full screen (`active` false -> true) and on every interaction; hidden `hideAfterMs` (4 s) after
 * the last one unless a hold is on. A new `itemKey` (next / auto-advance / wall item) shows once and restarts the
 * countdown. Outside full screen (`active` false) the controls count as shown and no timer runs.
 *
 * Mouse moves show, a touch / pen tap on the video toggles, a mouse press or any key shows. Keys are watched on the
 * root's whole document while in full screen: `requestFullscreen()` does not move focus, so it is often on `<body>` or
 * on the button that entered (outside the full-screen element) and a root-only handler would never see them.
 *
 * Focus on a control holds the controls up only when it came from the keyboard: a key was pressed, or the browser
 * marks it `:focus-visible`, or it arrived from outside the root (Tab out of a cross-origin iframe, whose keys we never
 * see). A focus right after a pointer press anywhere in the document never holds, and any pointer press releases it.
 */
export function usePlayerControlsVisibility(opts: { active: boolean; itemKey?: string; hideAfterMs?: number }): PlayerControlsVisibility {
  const { active, itemKey, hideAfterMs = PLAYER_CONTROLS_HIDE_AFTER_MS } = opts;
  const [visible, setVisible] = useState(true);
  const holds = useRef(new Set<HoldReason>());
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const activeRef = useRef(active);
  activeRef.current = active;
  const hideAfterRef = useRef(hideAfterMs);
  hideAfterRef.current = hideAfterMs;
  // The control keyboard focus is on (the "focus" hold): one that leaves the page without a blur (unmounted on an
  // item change) must not pin the controls.
  const focused = useRef<Element | null>(null);
  // The root (for its document).
  const rootEl = useRef<HTMLElement | null>(null);

  const clear = useCallback(() => {
    if (timer.current != null) clearTimeout(timer.current);
    timer.current = null;
  }, []);

  const restartTimer = useCallback(() => {
    clear();
    if (!activeRef.current || holds.current.size > 0) return;
    timer.current = setTimeout(() => {
      timer.current = null;
      visibleRef.current = false;
      setVisible(false);
    }, hideAfterRef.current);
  }, [clear]);

  // toggle reads the current visibility synchronously (two taps in one batch must not both "show").
  const visibleRef = useRef(visible);
  const setShown = useCallback((v: boolean) => {
    visibleRef.current = v;
    setVisible(v);
  }, []);

  const show = useCallback(() => {
    setShown(true);
    restartTimer();
  }, [setShown, restartTimer]);

  const toggle = useCallback(() => {
    if (!activeRef.current) return;
    if (!visibleRef.current) show();
    // Holds only stop the countdown: an explicit tap hides even paused / seeking / up-next / sheet controls.
    // Keyboard focus inside them is the exception (like TalkBack on Android).
    else if (!holds.current.has("focus")) {
      clear();
      setShown(false);
    }
  }, [show, clear, setShown]);

  const hold = useCallback((reason: HoldReason, on: boolean) => {
    if (on === holds.current.has(reason)) return;
    if (on) {
      holds.current.add(reason);
      clear();
      setShown(true);
    } else {
      holds.current.delete(reason);
      restartTimer();
    }
  }, [clear, restartTimer, setShown]);

  // Entering / leaving full screen.
  useEffect(() => {
    if (active) show();
    else {
      clear();
      setShown(true);
    }
  }, [active, show, clear, setShown]);

  // The focused control went away without a blur (React removes it silently): release its hold.
  const dropStaleFocus = useCallback(() => {
    if (holds.current.has("focus") && !focused.current?.isConnected) {
      focused.current = null;
      hold("focus", false);
    }
  }, [hold]);

  // A new item in full screen shows once (the first render is covered by the effect above).
  const lastItem = useRef(itemKey);
  useEffect(() => {
    if (lastItem.current === itemKey) return;
    lastItem.current = itemKey;
    dropStaleFocus();
    if (activeRef.current) show();
  }, [itemKey, show, dropStaleFocus]);

  // Any re-render can unmount the focused control (a button the new item lacks).
  useEffect(dropStaleFocus);

  useEffect(() => clear, [clear]);

  // Document-wide: a key anywhere shows the controls in full screen (I1); a pointer press anywhere (an arrow outside
  // the root, a thumbnail) is pointer use and drops a focus hold (I2).
  useEffect(() => {
    const doc = rootEl.current?.ownerDocument ?? (typeof document === "undefined" ? null : document);
    if (!doc) return;
    watchModality(doc);
    const onKey = () => {
      if (activeRef.current) show();
    };
    const onPointer = () => {
      focused.current = null;
      hold("focus", false);
    };
    doc.addEventListener("keydown", onKey, true);
    doc.addEventListener("pointerdown", onPointer, true);
    return () => {
      doc.removeEventListener("keydown", onKey, true);
      doc.removeEventListener("pointerdown", onPointer, true);
    };
  }, [active, show, hold]);

  const bind = useMemo<PlayerControlsVisibility["bind"]>(() => ({
    ref: (el) => {
      rootEl.current = el;
    },
    onPointerMove: (e) => {
      if (e.pointerType === "mouse") show();
    },
    onPointerDown: (e) => {
      // Back to pointer input: keyboard focus no longer pins the controls.
      markPointer();
      hold("focus", false);
      if (e.pointerType === "mouse" || onControl(e.target, e.currentTarget)) show();
      else toggle();
    },
    onKeyDown: () => {
      markKey();
      show();
    },
    onFocusCapture: (e) => {
      // Focus a mouse click left on a control (after Tab) does not keep the hold either.
      const byPointer = !modality.keyboard && Date.now() - modality.lastPointer < POINTER_FOCUS_MS;
      const fromOutside = !(e.relatedTarget instanceof Node && e.currentTarget.contains(e.relatedTarget));
      const kb = !byPointer && onControl(e.target, e.currentTarget) && (modality.keyboard || focusVisible(e.target) || fromOutside);
      focused.current = kb ? e.target : null;
      hold("focus", kb);
    },
    onBlurCapture: (e) => {
      // Moving between controls keeps the hold (the next focus re-takes it anyway).
      if (!onControl(e.relatedTarget, e.currentTarget)) {
        focused.current = null;
        hold("focus", false);
      }
    },
  }), [show, toggle, hold]);

  return { visible, show, toggle, interacted: show, hold, bind };
}

/**
 * The visibility of the full-screen controls around this element (FullScreenPlayerControls provides it), for controls
 * drawn by the player itself (the cast queue line's up-next list). null outside full screen.
 */
export const PlayerControlsContext = createContext<PlayerControlsVisibility | null>(null);

/** Holds the surrounding full-screen controls up for `reason` while `on` (and releases on unmount). */
export function usePlayerControlsHold(reason: HoldReason, on: boolean) {
  const hold = useContext(PlayerControlsContext)?.hold;
  useEffect(() => {
    if (!hold || !on) return;
    hold(reason, true);
    return () => hold(reason, false);
  }, [hold, reason, on]);
}
